import React, { useEffect, useState, useMemo, useRef } from "react";
import { Smile } from "lucide-react";
import api from "@/lib/api";

const SMILEY_RE = /:([a-z0-9_]{2,24}):/gi;

// Time-bounded cache so newly-added smilies show up within a minute
// without forcing a hard reload.
const SMILIE_CACHE_TTL_MS = 60_000;
let CACHED_SMILIES = null;
let CACHED_AT = 0;
let CACHED_PROMISE = null;

/** Force the next `useSmilies()` mount (or re-mount) to refetch. */
export function bustSmilieCache() {
  CACHED_SMILIES = null;
  CACHED_AT = 0;
  CACHED_PROMISE = null;
}

/** Fetch the global smilies list, cached for SMILIE_CACHE_TTL_MS. */
export function useSmilies() {
  const fresh = CACHED_SMILIES && (Date.now() - CACHED_AT) < SMILIE_CACHE_TTL_MS;
  const [smilies, setSmilies] = useState(fresh ? CACHED_SMILIES : []);
  useEffect(() => {
    if (CACHED_SMILIES && (Date.now() - CACHED_AT) < SMILIE_CACHE_TTL_MS) return;
    if (!CACHED_PROMISE) {
      CACHED_PROMISE = api.get("/chat/smilies")
        .then(({ data }) => {
          CACHED_SMILIES = Array.isArray(data) ? data : [];
          CACHED_AT = Date.now();
          return CACHED_SMILIES;
        })
        .catch(() => { CACHED_SMILIES = []; CACHED_AT = Date.now(); return []; })
        .finally(() => { CACHED_PROMISE = null; });
    }
    CACHED_PROMISE.then(setSmilies);
  }, []);
  const map = useMemo(
    () => Object.fromEntries(smilies.map((s) => [s.shortcode, s])),
    [smilies]
  );
  return { smilies, smilieMap: map };
}

/** Render plain text and replace `:shortcode:` with smilie images. */
export function renderWithSmilies(text, smilieMap) {
  if (!text) return null;
  const parts = [];
  let lastIdx = 0;
  const re = new RegExp(SMILEY_RE.source, "gi");
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    const sm = smilieMap[m[1].toLowerCase()];
    parts.push(
      sm ? (
        <img
          key={m.index}
          src={sm.url}
          alt={`:${m[1]}:`}
          title={`:${m[1]}:`}
          className="inline-block align-middle mx-0.5"
          style={{ height: "32px", width: "auto", maxWidth: "100%" }}
        />
      ) : (
        <span key={m.index} className="text-[var(--tt-text-muted)]">:{m[1]}:</span>
      )
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

/**
 * Smile button + popover picker. Calls `onInsert(":code:")` so the parent
 * textarea can splice it into its current text. `inputRef` is optional —
 * when provided, the focus moves back to the textarea after picking.
 */
export function SmilyPicker({ onInsert, inputRef, size = "default", testIdPrefix = "comment" }) {
  const { smilies } = useSmilies();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const pick = (code) => {
    onInsert(`:${code}: `);
    setOpen(false);
    inputRef?.current?.focus();
  };

  const btnSize = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  const iconSize = size === "sm" ? "w-5 h-5" : "w-7 h-7";

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testIdPrefix}-smiley-button`}
        title="Insert smilie"
        className={`${btnSize} rounded-full flex items-center justify-center text-[var(--tt-text-muted)] hover:text-[var(--tt-yellow)] hover:bg-[var(--tt-surface-2)] transition-colors`}
      >
        <Smile className={iconSize} />
      </button>
      {open && (
        <div
          data-testid={`${testIdPrefix}-smiley-picker`}
          className="absolute bottom-full mb-2 left-0 w-[280px] max-w-[80vw] z-30 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-xl p-2 max-h-44 overflow-y-auto grid grid-cols-8 gap-1 shadow-xl"
        >
          {smilies.length === 0 && (
            <div className="col-span-full text-xs text-[var(--tt-text-muted)] py-2 text-center">
              No smilies yet — ask an admin to add some.
            </div>
          )}
          {smilies.map((s) => (
            <button
              key={s.shortcode}
              type="button"
              onClick={() => pick(s.shortcode)}
              title={`:${s.shortcode}:`}
              data-testid={`${testIdPrefix}-smiley-${s.shortcode}`}
              className="h-9 min-w-9 px-1 flex items-center justify-center rounded hover:bg-white/10"
            >
              <img src={s.url} alt={s.shortcode} className="max-h-7 w-auto object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
